import React, { useState, useMemo, useEffect } from 'react';
import { visibilityBand } from '@/utils/visibilityBands';
import { Button } from '@/components/ui/button';
import { Loader2, Share2 } from 'lucide-react';
import { MilkyWayCalendar } from '@/components/MilkyWayCalendar';
import { LocationDisplayCard } from './LocationDisplayCard';
import { PlannerToolbar } from './PlannerToolbar';
import { YearlyCalendarView } from './YearlyCalendarView';
import { NoLocationSelected } from './NoLocationSelected';
import { NightVerdictPanel } from '@/components/dashboard/NightVerdictPanel';
import { MonthlyWeatherSummary } from '@/components/weather/MonthlyWeatherSummary';
import { useLocationManagement } from '@/hooks/useLocationManagement';
import { useAuth } from '@/contexts/AuthContext';
import { getLocationParts, getTimezone, getTimezoneFromCoordinates } from '@/utils/timeFormatting';
import { calculateMonthData, type DayData } from '@/utils/calendarData';
import { writeExcelFile, jsonToSheet, jsonToCSV, type ExcelSheet } from '@/utils/excelExport';
import { trackExportDownload } from '@/utils/analytics';
import { track, trackPlannerRun } from '@/lib/analytics';
import { useToast } from '@/hooks/use-toast';
import CalendarImportGuideModal from '../CalendarImportGuideModal';
import { CreateAnnualCalendarModal } from '@/components/CreateAnnualCalendarModal';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { edgeFunctionHeaders } from '@/lib/edgeFunctionHeaders';
import { coreDirectionColumns, coreDirectionLine, darkCoreColumns, darkCoreLabel } from '@/utils/coreDirection';

interface Location {
  id?: string;
  name: string;
  latitude: number;
  longitude: number;
  notes?: string | null;
  created_at?: string;
}

interface CalendarViewProps {
  selectedLocation: Location | null;
  onLocationUpdate?: (location: Location) => void;
  hideLocationCard?: boolean;
  selectedDay?: number;
  onDaySelect?: (dayDate: number) => void;
  onMonthChange?: (month: number, year: number) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  selectedLocation,
  onLocationUpdate,
  hideLocationCard = false,
  selectedDay,
  onDaySelect,
  onMonthChange,
}) => {
  const { user, isSubscribed } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [viewType, setViewType] = useState<'month' | 'year'>('month');
  const [selectedDate, setSelectedDate] = useState<{ month: number; year: number }>({
    month: new Date().getMonth(),
    year: new Date().getFullYear()
  });

  // Notify parent when month/year changes so map overlay uses correct data
  useEffect(() => {
    onMonthChange?.(selectedDate.month, selectedDate.year);
  }, [selectedDate.month, selectedDate.year, onMonthChange]);
  const [yearlyActiveMonth, setYearlyActiveMonth] = useState<number>(new Date().getMonth());
  const [isImportGuideOpen, setIsImportGuideOpen] = useState(false);
  const [showAnnualCalendarModal, setShowAnnualCalendarModal] = useState(false);
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);
  const [downloadedFileName, setDownloadedFileName] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  

  const {
    currentLocation,
    isNewLocationDialogOpen,
    setIsNewLocationDialogOpen,
    isFavoriteDialogOpen,
    setIsFavoriteDialogOpen,
    favoriteFormData,
    setFavoriteFormData,
    handleNewLocationSelect,
    handleStarLocation,
    refreshTrigger
  } = useLocationManagement(selectedLocation);

  // Calculate month data for export functionality
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthData = useMemo(() => {
    if (!currentLocation) return [];
    const month = viewType === 'year' ? yearlyActiveMonth : selectedDate.month;
    return calculateMonthData(currentLocation, month, selectedDate.year);
  }, [currentLocation, viewType, yearlyActiveMonth, selectedDate]);

  const getVisibilityRating = (moonPhase: number, galacticCoreStart: string) => {
    const moonPercent = Math.round(moonPhase * 100);
    
    if (galacticCoreStart === 'N/A' || 
        galacticCoreStart === '--:--' || 
        galacticCoreStart === 'Below horizon' ||
        galacticCoreStart === 'Not visible' ||
        galacticCoreStart === 'No twilight' ||
        galacticCoreStart === 'Not dark enough' ||
        galacticCoreStart === 'Too brief') return 0;
    if (moonPercent <= 5) return 10;
    else if (moonPercent <= 10) return 9;
    else if (moonPercent <= 15) return 8;
    else if (moonPercent <= 20) return 7;
    else if (moonPercent <= 25) return 6;
    else if (moonPercent <= 35) return 5;
    else if (moonPercent <= 45) return 4;
    else if (moonPercent <= 55) return 3;
    else if (moonPercent <= 70) return 2;
    else if (moonPercent <= 85) return 1;
    else return 0;
  };

  const getMoonPhaseName = (moonPhase: number) => {
    const percent = Math.round(moonPhase * 100);
    if (percent <= 1) return 'New Moon';
    else if (percent <= 25) return 'Waxing Crescent';
    else if (percent <= 49) return 'First Quarter';
    else if (percent <= 75) return 'Waxing Gibbous';
    else if (percent <= 99) return 'Full Moon';
    else return 'Waning';
  };

  // Was 9/7/5/3, one point above the legend a reader sees beside the colours.
  const getQualityDescription = (rating: number) => visibilityBand(rating);

  const formatLocationName = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  };

  const generateFilename = (format: string) => {
    if (!currentLocation) return '';
    const locationFormatted = formatLocationName(currentLocation.name);
    if (viewType === 'year') {
      return `milky-way-planner-${locationFormatted}_yearly_${selectedDate.year}.${format}`;
    } else {
      const monthName = monthNames[selectedDate.month].toLowerCase();
      return `milky-way-planner-${locationFormatted}-${monthName}-${selectedDate.year}.${format}`;
    }
  };

  const exportToCSV = () => {
    if (!currentLocation) return;
    
    const filename = generateFilename('csv');
    trackExportDownload('csv', currentLocation.name);
    
    const csvData = monthData.map(day => ({
      'Date': day.fullDate,
      'Sunrise': day.sunrise,
      'Sunset': day.sunset,
      'Moonrise': day.moonrise,
      'Moonset': day.moonset,
      'Core Start': day.galacticCoreStart,
      'Core End': day.galacticCoreEnd,
      ...darkCoreColumns(day),
      ...coreDirectionColumns(day),
      'Moon %': `${Math.round(day.moonPhase * 100)}%`,
      'Visibility Rating': getVisibilityRating(day.moonPhase, day.galacticCoreStart)
    }));
    
    const csv = jsonToCSV(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToExcel = async () => {
    if (!currentLocation) return;

    const filename = generateFilename('xlsx');
    trackExportDownload('xlsx', currentLocation.name);

    const sheets: ExcelSheet[] = [];

    // Summary/info tab
    sheets.push({
      name: 'MILKY WAY PLANNER',
      data: [
        ['MILKY WAY PLANNER - CALENDAR EXPORT'],
        [''],
        ['LOCATION INFORMATION'],
        [`Location: ${currentLocation.name}`],
        [`Coordinates: ${currentLocation.latitude.toFixed(4)}°N, ${currentLocation.longitude.toFixed(4)}°E`],
        [`Month: ${viewType === 'year' ? `Full Year ${selectedDate.year}` : `${monthNames[selectedDate.month]} ${selectedDate.year}`}`],
        [''],
        ['HOW TO USE THIS PLANNER'],
        [''],
        ['Best Photography Windows:'],
        ['The Milky Way\'s galactic core is visible during astronomical darkness,'],
        ['but you must also check that the moon is not up during these times.'],
        [''],
        ['Moon Timing is Critical:'],
        ['The best astrophotography happens when the moon is NOT in the sky'],
        ['during your shooting window. Even a small crescent moon can wash out'],
        ['the Milky Way. Check both moonrise/moonset times AND the core visibility window.'],
        [''],
        ['Ideal Conditions:'],
        ['Perfect nights have: Core Start/End times available + Moon percentage'],
        ['15% or below + Moon not visible during your shooting window.'],
        ['This combination gives maximum contrast and detail.'],
        [''],
        ['Pro Tips:'],
        ['• Plan shoots during new moon phases (0-10%)'],
        ['• Verify moon has set before your shooting window'],
        ['• Or shoot before moonrise if moon rises late'],
        ['• Arrive before the "Core Start" time to set up'],
        ['• Check weather conditions separately'],
        ['• Allow 20-30 minutes for your eyes to adjust to darkness'],
        ['• Best viewing is away from city lights'],
        [''],
        ['Legend:'],
        ['• Astronomical Twilight: When the sun is 18° below horizon - true dark sky begins'],
        ['• Milky Way Core: Optimal viewing window during astronomical darkness'],
        ['• Moon Phase: Percentage of lunar illumination (0% = New Moon, 100% = Full Moon)'],
        [''],
        ['Generated by MilkyWayPlanner.com']
      ],
    });

    if (viewType === 'year') {
      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const monthlyData = calculateMonthData(currentLocation, monthIndex, selectedDate.year);
        const sheet = jsonToSheet(monthlyData.map(day => {
          const rating = getVisibilityRating(day.moonPhase, day.galacticCoreStart);
          const moonPercent = Math.round(day.moonPhase * 100);
          return {
            'Date': `${monthNames[monthIndex]} ${day.date}`,
            'Sunrise': day.sunrise, 'Sunset': day.sunset,
            'Moonrise': day.moonrise, 'Moonset': day.moonset,
            'Core Start': day.galacticCoreStart, 'Core End': day.galacticCoreEnd,
            ...darkCoreColumns(day),
            ...coreDirectionColumns(day),
            'Moon %': `${moonPercent}%`, 'Phase Name': getMoonPhaseName(day.moonPhase),
            'Visibility Rating': rating, 'Visibility Bars': '█'.repeat(rating) + '░'.repeat(10 - rating)
          };
        }));
        sheet.name = `${monthNames[monthIndex]} ${selectedDate.year}`;
        sheets.push(sheet);
      }

      const allYearData: Array<DayData & { monthName: string }> = [];
      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const monthlyData = calculateMonthData(currentLocation, monthIndex, selectedDate.year);
        allYearData.push(...monthlyData.map(day => ({ ...day, monthName: monthNames[monthIndex] })));
      }

      const bestDays = allYearData
        .filter(day => getVisibilityRating(day.moonPhase, day.galacticCoreStart) >= 5)
        .sort((a, b) => getVisibilityRating(b.moonPhase, b.galacticCoreStart) - getVisibilityRating(a.moonPhase, a.galacticCoreStart))
        .map((day, index) => ({
          'Rank': `#${index + 1}`, 'Date': `${day.monthName} ${day.date}`,
          'Quality': getQualityDescription(getVisibilityRating(day.moonPhase, day.galacticCoreStart)),
          'Rating': `${getVisibilityRating(day.moonPhase, day.galacticCoreStart)}/10`,
          'Moon %': `${Math.round(day.moonPhase * 100)}%`, 'Phase': getMoonPhaseName(day.moonPhase),
          'Core Start': day.galacticCoreStart, 'Core End': day.galacticCoreEnd
        }));

      if (bestDays.length > 0) {
        const bestSheet = jsonToSheet(bestDays);
        bestSheet.name = 'BEST NIGHTS';
        sheets.push(bestSheet);
      }
    } else {
      const sheet = jsonToSheet(monthData.map(day => {
        const rating = getVisibilityRating(day.moonPhase, day.galacticCoreStart);
        const moonPercent = Math.round(day.moonPhase * 100);
        return {
          'Date': day.fullDate, 'Sunrise': day.sunrise, 'Sunset': day.sunset,
          'Moonrise': day.moonrise, 'Moonset': day.moonset,
          'Core Start': day.galacticCoreStart, 'Core End': day.galacticCoreEnd,
          ...darkCoreColumns(day),
          ...coreDirectionColumns(day),
          'Moon %': `${moonPercent}%`, 'Phase Name': getMoonPhaseName(day.moonPhase),
          'Visibility Rating': rating, 'Visibility Bars': '█'.repeat(rating) + '░'.repeat(10 - rating)
        };
      }));
      sheet.name = `${monthNames[selectedDate.month]} ${selectedDate.year}`;
      sheets.push(sheet);

      const bestNights = monthData
        .filter(day => getVisibilityRating(day.moonPhase, day.galacticCoreStart) >= 5)
        .sort((a, b) => getVisibilityRating(b.moonPhase, b.galacticCoreStart) - getVisibilityRating(a.moonPhase, a.galacticCoreStart))
        .map((day, index) => ({
          'Rank': `#${index + 1}`, 'Date': day.fullDate,
          'Quality': getQualityDescription(getVisibilityRating(day.moonPhase, day.galacticCoreStart)),
          'Rating': `${getVisibilityRating(day.moonPhase, day.galacticCoreStart)}/10`,
          'Moon %': `${Math.round(day.moonPhase * 100)}%`, 'Phase': getMoonPhaseName(day.moonPhase),
          'Core Start': day.galacticCoreStart, 'Core End': day.galacticCoreEnd
        }));

      if (bestNights.length > 0) {
        const bestSheet = jsonToSheet(bestNights);
        bestSheet.name = 'BEST NIGHTS';
        sheets.push(bestSheet);
      }
    }

    await writeExcelFile(sheets, filename);
  };

  const exportToICS = async () => {
    if (!currentLocation) return;
    
    setIsExporting(true);

    try {
      const filename = generateFilename('ics');
      trackExportDownload('ics', currentLocation.name);
      
      let goodDays = [];
      
      if (viewType === 'year') {
        // For yearly view, get good days from all months
        for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
          const monthlyData = calculateMonthData(currentLocation, monthIndex, selectedDate.year);
          const monthlyGoodDays = monthlyData
            .filter(day => getVisibilityRating(day.moonPhase, day.galacticCoreStart) >= 4)
            .map(day => ({ ...day, month: monthIndex }));
          goodDays.push(...monthlyGoodDays);
        }
      } else {
        // For monthly view, use current month data
        goodDays = monthData.filter(day => getVisibilityRating(day.moonPhase, day.galacticCoreStart) >= 4);
      }
      
      if (goodDays.length === 0) {
        toast({
          title: "No events to export",
          description: "No good visibility days found for the selected period. Try a different month or location.",
          variant: "destructive"
        });
        return;
      }

      const formatDateForICS = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
      };

      const formatDateTimeForICS = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
      };

      const escapeICSText = (text: string) => {
        return text.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
      };

      const generateVTimezone = () => {
        return [
          'BEGIN:VTIMEZONE',
          'TZID:UTC',
          'BEGIN:STANDARD',
          'DTSTART:19700101T000000',
          'TZOFFSETFROM:+0000',
          'TZOFFSETTO:+0000',
          'TZNAME:UTC',
          'END:STANDARD',
          'END:VTIMEZONE'
        ].join('\r\n');
      };

      const createICSEvent = (day: DayData & { month?: number }) => {
        const rating = getVisibilityRating(day.moonPhase, day.galacticCoreStart);
        const moonPercent = Math.round(day.moonPhase * 100);
        const starRating = '⭐'.repeat(Math.min(rating, 10)) || '☆';
        
        const eventMonth = day.month !== undefined ? day.month : selectedDate.month;
        const eventDate = new Date(selectedDate.year, eventMonth, day.date);
        const startDate = formatDateForICS(eventDate);
        
        const endDate = new Date(eventDate);
        endDate.setDate(endDate.getDate() + 1);
        const endDateFormatted = formatDateForICS(endDate);

        const title = `🌌 Milky Way Photography ${starRating}`;
        
        const description = 
          `🌌 OPTIMAL MILKY WAY PHOTOGRAPHY CONDITIONS` +
          `\\n\\n📍 LOCATION: ${currentLocation.name}` +
          `\\n📐 Coordinates: ${currentLocation.latitude.toFixed(4)}°, ${currentLocation.longitude.toFixed(4)}°` +
          `\\n\\n⭐ VISIBILITY: ${starRating} (${rating}/10)` +
          `\\n🌙 Moon Phase: ${moonPercent}%` +
          `\\n\\n⏰ TIMING` +
          `\\n🌌 Core Visibility: ${day.galacticCoreStart} - ${day.galacticCoreEnd}` +
          (darkCoreLabel(day) ? `\\n🌑 ${darkCoreLabel(day)}` : '') +
          (coreDirectionLine(day) ? `\\n🧭 Point: ${coreDirectionLine(day)}` : '') +
          `\\n🌅 Sunrise: ${day.sunrise}` +
          `\\n🌇 Sunset: ${day.sunset}` +
          `\\n\\n💡 PHOTOGRAPHY TIPS` +
          `\\n• Arrive 30 minutes before core visibility starts` +
          `\\n• Check weather conditions separately` +
          `\\n• Find a location away from city lights` +
          `\\n• Allow 20-30 minutes for eyes to adjust to darkness` +
          `\\n• Use a red flashlight to preserve night vision` +
          `\\n\\n📱 Plan your shoot at: https://www.milkywayplanner.com` +
          `\\n\\nGenerated by MilkyWayPlanner.com`;

        // Create reminder alarm for 8 PM
        const reminderTime = new Date(eventDate);
        reminderTime.setHours(20, 0, 0, 0);
        const alarmTime = formatDateTimeForICS(reminderTime);

        const priority = rating >= 8 ? 'HIGH' : rating >= 6 ? 'NORMAL' : 'LOW';

        return [
          'BEGIN:VEVENT',
          `UID:milkyway-${eventDate.getTime()}-${currentLocation.latitude}-${currentLocation.longitude}@milkywayplanner.com`,
          `DTSTAMP:${formatDateTimeForICS(new Date())}`,
          `CREATED:${formatDateTimeForICS(new Date())}`,
          `LAST-MODIFIED:${formatDateTimeForICS(new Date())}`,
          `DTSTART;VALUE=DATE:${startDate}`,
          `DTEND;VALUE=DATE:${endDateFormatted}`,
          `SUMMARY:${escapeICSText(title)}`,
          `DESCRIPTION:${escapeICSText(description)}`,
          `LOCATION:${escapeICSText(`${currentLocation.name} (${currentLocation.latitude.toFixed(4)}°, ${currentLocation.longitude.toFixed(4)}°)`)}`,
          `GEO:${currentLocation.latitude};${currentLocation.longitude}`,
          'CATEGORIES:Astrophotography,Milky Way,Photography,Planning',
          `PRIORITY:${priority === 'HIGH' ? '1' : priority === 'NORMAL' ? '5' : '9'}`,
          'STATUS:CONFIRMED',
          'TRANSP:TRANSPARENT',
          'CLASS:PUBLIC',
          'SEQUENCE:0',
          // Add reminder alarm
          'BEGIN:VALARM',
          'ACTION:DISPLAY',
          `DESCRIPTION:Milky Way photography tonight! Visibility: ${starRating}`,
          `TRIGGER;VALUE=DATE-TIME:${alarmTime}`,
          'END:VALARM',
          'END:VEVENT'
        ].join('\r\n');
      };

      const calendarTitle = viewType === 'year' 
        ? `Milky Way Photography Calendar ${selectedDate.year} - ${currentLocation.name}`
        : `Milky Way Photography Calendar ${monthNames[selectedDate.month]} ${selectedDate.year} - ${currentLocation.name}`;

      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//MilkyWayPlanner.com//Milky Way Photography Calendar V1.0//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        `X-WR-CALNAME:${escapeICSText(calendarTitle)}`,
        `X-WR-CALDESC:${escapeICSText('Optimal Milky Way photography dates with visibility ratings and detailed timing information')}`,
        'X-WR-TIMEZONE:UTC',
        'X-WR-RELCALID:' + Math.random().toString(36).substr(2, 9),
        generateVTimezone(),
        ...goodDays.map(day => createICSEvent(day)),
        'END:VCALENDAR'
      ].join('\r\n');

      // Calculate file size for user info
      const fileSizeKB = Math.round(new Blob([icsContent]).size / 1024);

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success message with file info
      toast({
        title: "Calendar exported successfully!",
        description: `Downloaded ${filename} (${fileSizeKB}KB, ${goodDays.length} events)`,
        duration: 5000,
      });

      setDownloadedFileName(filename);
      setIsImportGuideOpen(true);

    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: "Export failed",
        description: "There was an error creating your calendar file. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!currentLocation) return;
    
    setIsDownloadingPDF(true);
    try {
      // In month view, scope the PDF to the selected month; in yearly view, omit
      // `month` so the edge function generates the full annual calendar (ERI-1524).
      const isMonthView = viewType === 'month';
      const params = {
        latitude: currentLocation.latitude.toString(),
        longitude: currentLocation.longitude.toString(),
        location_name: currentLocation.name,
        year: selectedDate.year.toString(),
        ...(isMonthView ? { month: selectedDate.month.toString() } : {}),
      };

      trackExportDownload('pdf', currentLocation.name);

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/generate-calendar-pdf`,
        {
          method: 'POST',
          headers: await edgeFunctionHeaders(),
          body: JSON.stringify(params),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Binary handling for PDF
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Generate filename - month-scoped in month view, otherwise full year
      const safeLocationName = currentLocation.name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
      const filename = isMonthView
        ? `${safeLocationName}-${monthNames[selectedDate.month].toLowerCase()}-${selectedDate.year}-milky-way-calendar.pdf`
        : `${safeLocationName}-${selectedDate.year}-milky-way-calendar.pdf`;
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      track('calendar_pdf_download', { year: selectedDate.year });

      toast({
        title: 'PDF downloaded successfully!',
        description: isMonthView
          ? `Your ${monthNames[selectedDate.month]} ${selectedDate.year} calendar has been downloaded.`
          : 'Your annual calendar has been downloaded.',
      });
    } catch (error) {
      console.error('PDF download failed:', error);
      toast({
        title: 'Failed to download PDF',
        description: 'There was an error generating your PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleCopyLink = async () => {
    if (!currentLocation) return;
    
    const baseUrl = 'https://www.milkywayplanner.com';
    const month = viewType === 'year' ? yearlyActiveMonth : selectedDate.month;
    const params = new URLSearchParams({
      location: currentLocation.name,
      lat: currentLocation.latitude.toString(),
      lon: currentLocation.longitude.toString(),
      month: month.toString(),
      year: selectedDate.year.toString()
    });
    const shareUrl = `${baseUrl}/?${params.toString()}`;
    
    // Copy Link must COPY — never open the OS share sheet (ERI-1525). Primary path is the
    // async Clipboard API; fall back to a hidden-textarea execCommand copy if it's blocked.
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        copied = true;
      }
    } catch {
      // fall through to the legacy copy below
    }
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        copied = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        copied = false;
      }
    }

    toast(
      copied
        ? { title: 'Link copied!', description: 'Share this plan with others.' }
        : { title: 'Unable to copy', description: 'Please copy the URL from your browser.', variant: 'destructive' }
    );
  };

  const handleCreateAnnualCalendar = async () => {
    if (!currentLocation) return;
    
    // For premium users with email, create directly
    if (user && isSubscribed) {
      setIsCreatingCalendar(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userEmail = session?.user?.email;
        
        if (userEmail) {
          const { data, error } = await supabase.functions.invoke('create-shared-calendar', {
            body: {
              email: userEmail,
              location_name: currentLocation.name,
              latitude: currentLocation.latitude,
              longitude: currentLocation.longitude,
              year: selectedDate.year,
              user_id: user.id,
              is_premium_user: true,
              subscribe_newsletter: false
            }
          });
          
          if (error) throw error;
          
          toast({
            title: 'Annual Calendar Created!',
            description: 'Redirecting to your calendar...',
          });
          
          navigate(`/calendar/${data.slug}`);
          return;
        }
      } catch (error) {
        console.error('Error creating calendar:', error);
        toast({
          title: 'Error',
          description: 'Failed to create calendar. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setIsCreatingCalendar(false);
      }
    }
    
    // For non-premium users, show the modal
    setShowAnnualCalendarModal(true);
  };

  const handleLocationSelectWithUpdate = (location: { name: string; latitude: number; longitude: number } | null) => {
    handleNewLocationSelect(location);

    if (location) {
      trackPlannerRun('custom', location.name);
    }

    if (location && onLocationUpdate) {
      const locationForParent = {
        id: `temp_${Date.now()}`,
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        notes: null,
        created_at: new Date().toISOString()
      };
      onLocationUpdate(locationForParent);
    }
  };

  if (!currentLocation) {
    return (
      <NoLocationSelected
        isNewLocationDialogOpen={isNewLocationDialogOpen}
        setIsNewLocationDialogOpen={setIsNewLocationDialogOpen}
        onNewLocationSelect={handleLocationSelectWithUpdate}
      />
    );
  }

  const locationParts = getLocationParts(currentLocation.name);
  const timezone = getTimezone(currentLocation.latitude, currentLocation.longitude);

  return (
    <div className="space-y-6">
      {/* Location Info - only show if not hidden by parent */}
      {!hideLocationCard && (
        <LocationDisplayCard
          location={currentLocation}
          user={user}
          isFavoriteDialogOpen={isFavoriteDialogOpen}
          setIsFavoriteDialogOpen={setIsFavoriteDialogOpen}
          favoriteFormData={favoriteFormData}
          setFavoriteFormData={setFavoriteFormData}
          onStarLocation={handleStarLocation}
          key={`${currentLocation.latitude}-${currentLocation.longitude}-${refreshTrigger}`}
        />
      )}

      {/* Unified Toolbar: nav + location + exports + month tabs */}
      <PlannerToolbar
        viewType={viewType}
        setViewType={setViewType}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        locationName={`${locationParts.city}${locationParts.stateProvince ? `, ${locationParts.stateProvince}` : ''}`}
        coordinates={{ lat: currentLocation.latitude, lon: currentLocation.longitude }}
        timezone={timezone}
        isSubscribed={!!(user && isSubscribed)}
        onDownloadPDF={handleDownloadPDF}
        onExportExcel={exportToExcel}
        onExportCSV={exportToCSV}
        onExportICS={exportToICS}
        onCopyLink={handleCopyLink}
        onShareCalendar={handleCreateAnnualCalendar}
        onOpenImportGuide={() => setIsImportGuideOpen(true)}
        isDownloadingPDF={isDownloadingPDF}
        isExporting={isExporting}
        isCreatingCalendar={isCreatingCalendar}
        icsEventCount={monthData.filter(day => getVisibilityRating(day.moonPhase, day.galacticCoreStart) >= 4).length}
      />

      {/* Tonight / tomorrow night weather verdict for Premium Users (ERI-1980) */}
      {user && isSubscribed && currentLocation?.id && viewType === 'month' && (
        <NightVerdictPanel
          locationId={currentLocation.id}
          locationName={currentLocation.name}
          latitude={currentLocation.latitude}
          longitude={currentLocation.longitude}
          timezone={getTimezoneFromCoordinates(currentLocation.latitude, currentLocation.longitude)}
        />
      )}

      {/* Weather Summary for Premium Users - Monthly view only */}
      {user && isSubscribed && currentLocation?.id && viewType === 'month' && (
        <MonthlyWeatherSummary
          locationId={currentLocation.id}
          month={selectedDate.month}
          year={selectedDate.year}
          locationName={currentLocation.name}
        />
      )}

      {/* Calendar Display */}
      {viewType === 'month' ? (
        <MilkyWayCalendar
          location={{
            name: currentLocation.name,
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude
          }}
          month={selectedDate.month}
          year={selectedDate.year}
          selectedDay={selectedDay}
          onDaySelect={onDaySelect}
        />
      ) : (
        <YearlyCalendarView
          location={currentLocation}
          year={selectedDate.year}
          yearlyActiveMonth={yearlyActiveMonth}
          setYearlyActiveMonth={setYearlyActiveMonth}
        />
      )}


      {/* Annual Calendar Modal */}
      <CreateAnnualCalendarModal
        location={{
          name: currentLocation.name,
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude
        }}
        selectedYear={selectedDate.year}
        isOpen={showAnnualCalendarModal}
        onClose={() => setShowAnnualCalendarModal(false)}
      />

      {/* Calendar Import Guide Modal */}
      <CalendarImportGuideModal
        isOpen={isImportGuideOpen}
        onClose={() => setIsImportGuideOpen(false)}
        fileName={downloadedFileName}
      />
    </div>
  );
};
